import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Shared modal anatomy for the admin app. `dialog.tsx` stays the unopinionated
 * Radix wrapper (overlay, focus trap, X close); this layer encodes the house
 * style — a divided header, a single scrolling body, and a bordered footer.
 *
 * Canonical structure (the modal owns `useForm` and wraps everything in a
 * `FormProvider`, so the footer auto-derives loading from `formState.isSubmitting`
 * and submits via onClick — no `form` id linkage needed):
 *   const methods = useForm({ resolver, defaultValues });
 *   const onValid = methods.handleSubmit(async v => { await mutate(v); onClose(); });
 *   <Dialog open onOpenChange={o => !o && tryClose()}>
 *     <ModalContent size="lg">
 *       <FormProvider {...methods}>
 *         <ModalHeader title="…" description="…" />
 *         <ModalBody><form onSubmit={onValid}>…</form></ModalBody>
 *         <ModalFooter onCancel={tryClose} onSubmit={onValid} submitLabel="Save" />
 *       </FormProvider>
 *     </ModalContent>
 *   </Dialog>
 */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '5xl' | '6xl';

const SIZE_MAP: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
};

interface ModalContentProps extends React.ComponentPropsWithoutRef<typeof DialogContent> {
  size?: ModalSize;
}

/**
 * Flex-column scroll shell. Scrolling lives in ModalBody, so the content itself
 * is `overflow-hidden` with a fixed max-height; the inherited X close button
 * stays at the top-right.
 *
 * The height is `dvh`, not `vh`: on a phone `100vh` is the viewport with the browser's toolbar
 * hidden, so a modal sized to it hangs behind that toolbar and the footer's Save button ends up
 * under the address bar — reachable only by scrolling the page first. `dvh` is the space actually
 * on screen, and it re-measures as the toolbar slides away.
 */
const ModalContent = React.forwardRef<React.ElementRef<typeof DialogContent>, ModalContentProps>(
  ({ className, size = 'lg', ...props }, ref) => (
    <DialogContent
      ref={ref}
      className={cn(
        'top-3 flex max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] translate-y-0 flex-col gap-0 overflow-hidden border border-border/80 bg-background/95 p-0 shadow-[0_32px_96px_rgba(15,23,42,0.24)] supports-[backdrop-filter]:bg-background/92 sm:top-8 sm:max-h-[calc(100dvh-4rem)] sm:w-full',
        SIZE_MAP[size],
        className
      )}
      {...props}
    />
  )
);
ModalContent.displayName = 'ModalContent';

interface ModalHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
}

/** Divided header. `pr-12` reserves room so the title never collides with the X. */
const ModalHeader = ({ title, description, className, children, ...props }: ModalHeaderProps) => (
  <div
    className={cn(
      'shrink-0 border-b border-border/80 bg-background/95 px-6 py-5 pr-16 supports-[backdrop-filter]:bg-background/88 sm:px-7',
      className
    )}
    {...props}
  >
    <DialogTitle className="text-[1.75rem] font-semibold leading-tight tracking-[-0.03em]">{title}</DialogTitle>
    {description ? (
      <DialogDescription className="mt-1.5 max-w-3xl text-sm leading-6">{description}</DialogDescription>
    ) : null}
    {children}
  </div>
);
ModalHeader.displayName = 'ModalHeader';

/**
 * The only scrolling region. `min-h-0` is required for the flex child to scroll.
 *
 * `relative` keeps absolutely positioned children (an `sr-only` file input above all) inside this
 * scroller. Without it their containing block is the fixed shell, so when focus returns to such an
 * input after the file picker closes, the browser scrolls the `overflow-hidden` shell itself to
 * reveal it — the header slides out of view and a blank band opens under the footer.
 */
const ModalBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6 sm:px-7', className)}
    {...props}
  />
);
ModalBody.displayName = 'ModalBody';

interface ModalFooterProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  /** Cancel handler. Omit to render no Cancel button (rare). */
  onCancel?: () => void;
  cancelLabel?: string;
  submitLabel?: string;
  loadingLabel?: string;
  isLoading?: boolean;
  submitDisabled?: boolean;
  submitVariant?: 'default' | 'destructive';
  /** When set, the submit button associates with this form id via `type="submit"`. */
  form?: string;
  /** Used when the submit is not tied to a <form> element. */
  onSubmit?: () => void;
}

/**
 * Bordered, right-aligned footer.
 *  - Canonical mode: pass onCancel + submitLabel (+ form or onSubmit).
 *  - Passthrough mode: pass `children` to supply custom action buttons.
 * Cancel is always `variant="outline"`.
 *
 * Loading: when rendered inside a `<FormProvider>` the footer auto-derives its
 * loading state from `formState.isSubmitting` (the async submit handler), so most
 * callers don't need to pass `isLoading`. An explicit `isLoading` prop overrides it.
 */
const ModalFooter = ({
  className,
  children,
  onCancel,
  cancelLabel,
  submitLabel,
  loadingLabel,
  isLoading,
  submitDisabled = false,
  submitVariant = 'default',
  form,
  onSubmit,
  ...props
}: ModalFooterProps) => {
  const { t } = useTranslation();
  // null outside a FormProvider — safe for non-form footers (DeleteDialog, info viewers).
  const formCtx = useFormContext();
  const loading = isLoading ?? formCtx?.formState.isSubmitting ?? false;
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const resolvedLoadingLabel = loadingLabel ?? t('common.saving');

  return (
    <div
      className={cn(
        // The bottom padding clears the home indicator on a phone, where this footer is the last
        // thing above the edge of the screen; it collapses to the usual py-4 everywhere else.
        'flex shrink-0 flex-col-reverse gap-3 border-t border-border/80 bg-background/95 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-12px_30px_rgba(15,23,42,0.06)] supports-[backdrop-filter]:bg-background/88 sm:flex-row sm:items-center sm:justify-end sm:px-7',
        className
      )}
      {...props}
    >
      {children ?? (
        <>
          {onCancel ? (
            <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onCancel} disabled={loading}>
              {resolvedCancelLabel}
            </Button>
          ) : null}
          {submitLabel ? (
            <Button
              type={form ? 'submit' : 'button'}
              form={form}
              variant={submitVariant}
              className="w-full sm:w-auto"
              onClick={form ? undefined : onSubmit}
              disabled={loading || submitDisabled}
            >
              {loading ? resolvedLoadingLabel : submitLabel}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
};
ModalFooter.displayName = 'ModalFooter';

export { Dialog, ModalContent, ModalHeader, ModalBody, ModalFooter };
